import React, { useRef, useState, useEffect } from 'react';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { XIcon, MinusIcon } from '@phosphor-icons/react';

interface DynamicIslandGeneratorProps {
    isOpen: boolean;
    onClose: () => void;
    onMinimize?: () => void;
    children: React.ReactNode;
}

export const DynamicIslandGenerator: React.FC<DynamicIslandGeneratorProps> = ({
    isOpen,
    onClose,
    onMinimize,
    children
}) => {
    const dragControls = useDragControls();
    const panelRef = useRef<HTMLDivElement>(null);
    const constraintsRef = useRef<HTMLDivElement>(null);

    // Panel width state for resize
    const [panelWidth, setPanelWidth] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('ezart_generator_island_width');
            return saved ? parseInt(saved) : 480;
        }
        return 480;
    });

    // Save width to localStorage
    useEffect(() => {
        localStorage.setItem('ezart_generator_island_width', String(panelWidth));
    }, [panelWidth]);

    // Sidebar state for positioning the constraints area
    const [sidebarState, setSidebarState] = useState(() => {
        if (typeof window !== 'undefined') {
            const isSidebarOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
            const isRightPanelOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
            const navPosition = localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left';
            return { isSidebarOpen, isRightPanelOpen, navPosition };
        }
        return { isSidebarOpen: true, isRightPanelOpen: false, navPosition: 'left' as const };
    });

    // Listen for sidebar state changes (less frequent polling)
    useEffect(() => {
        const updateSidebarState = () => {
            const isSidebarOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
            const isRightPanelOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
            const navPosition = localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left';
            setSidebarState(prev => {
                if (prev.isSidebarOpen !== isSidebarOpen || prev.isRightPanelOpen !== isRightPanelOpen || prev.navPosition !== navPosition) {
                    return { isSidebarOpen, isRightPanelOpen, navPosition };
                }
                return prev;
            });
        };

        window.addEventListener('storage', updateSidebarState);
        // Poll less frequently to reduce jitter
        const interval = setInterval(updateSidebarState, 500);

        return () => {
            window.removeEventListener('storage', updateSidebarState);
            clearInterval(interval);
        };
    }, []);

    // Constants
    const SIDEBAR_WIDTH = 240;
    const RIGHT_PANEL_WIDTH = 640; // Right panel is wider
    const margin = 16;

    // Calculate constraint area margins based on nav position
    const leftMargin = sidebarState.navPosition === 'left'
        ? (sidebarState.isSidebarOpen ? SIDEBAR_WIDTH + margin : margin)
        : (sidebarState.isRightPanelOpen ? RIGHT_PANEL_WIDTH + margin : margin);
    const rightMargin = sidebarState.navPosition === 'left'
        ? (sidebarState.isRightPanelOpen ? RIGHT_PANEL_WIDTH + margin : margin)
        : (sidebarState.isSidebarOpen ? SIDEBAR_WIDTH + margin : margin);

    return (
        <>
            {/* Constraints container - this div defines the draggable area */}
            <div
                ref={constraintsRef}
                className="fixed inset-0 pointer-events-none z-[4999]"
                style={{
                    left: leftMargin,
                    right: rightMargin,
                    top: margin,
                    bottom: margin
                }}
            />

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        ref={panelRef}
                        drag
                        dragControls={dragControls}
                        dragListener={false}
                        dragConstraints={constraintsRef}
                        dragElastic={0.05}
                        dragMomentum={false}
                        initial={{
                            opacity: 0,
                            scale: 0.95,
                            // Start centered horizontally in the available space, near bottom
                            left: `calc(50% + ${(leftMargin - rightMargin) / 2}px)`,
                            bottom: margin + 20,
                            x: '-50%'
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            x: '-50%'
                        }}
                        exit={{
                            opacity: 0,
                            scale: 0.95
                        }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        style={{
                            width: panelWidth,
                            minWidth: '380px',
                        }}
                        className="fixed z-[5000] rounded-2xl shadow-2xl shadow-black/50 border border-white/10 group pointer-events-auto flex flex-col overflow-hidden bg-onyx"
                    >
                        {/* Title Bar / Drag Handle */}
                        <div
                            onPointerDown={(e) => dragControls.start(e)}
                            className="h-9 w-full bg-gradient-to-b from-zinc-900 to-neutral-900 border-b border-white/5 flex items-center justify-between px-3 cursor-grab active:cursor-grabbing z-50 shrink-0 select-none"
                        >
                            {/* Decorative Window Controls (Mac-like) */}
                            <div className="flex gap-1.5">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    className="w-3 h-3 rounded-full bg-[#ff5f57] hover:bg-[#ff5f57]/80 transition-colors flex items-center justify-center group/close"
                                    title="Close"
                                >
                                    <XIcon className="w-2 h-2 text-[#990000] opacity-0 group-hover/close:opacity-100 transition-opacity" weight="bold" />
                                </button>
                                {onMinimize && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onMinimize(); }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="w-3 h-3 rounded-full bg-[#febc2e] hover:bg-[#febc2e]/80 transition-colors flex items-center justify-center group/min"
                                        title="Minimize"
                                    >
                                        <MinusIcon className="w-2 h-2 text-[#995700] opacity-0 group-hover/min:opacity-100 transition-opacity" weight="bold" />
                                    </button>
                                )}
                                <div className="w-3 h-3 rounded-full bg-zinc-700" />
                            </div>

                            {/* Centered Title / Drag Indicator */}
                            <div className="flex-1 flex justify-center">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Generator</span>
                                </div>
                            </div>

                            {/* Spacer for symmetry */}
                            <div className="w-[52px]" />
                        </div>

                        {/* Content Area - Generator Input will be placed here */}
                        <div className="flex-1 overflow-hidden">
                            {children}
                        </div>

                        {/* Resize Handle (Bottom Right) */}
                        <motion.div
                            drag="x"
                            dragMomentum={false}
                            dragElastic={0}
                            dragConstraints={{ left: 0, right: 0 }}
                            onDrag={(e, info) => {
                                setPanelWidth(prev => Math.max(380, Math.min(700, prev + info.delta.x)));
                            }}
                            className="absolute bottom-0 right-0 w-6 h-6 z-50 cursor-nwse-resize flex items-center justify-center hover:bg-white/5 rounded-br-2xl rounded-tl-lg transition-colors pointer-events-auto"
                        >
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-30 group-hover:opacity-50 transition-opacity">
                                <path d="M8 8L0 8L8 0L8 8Z" fill="white" />
                            </svg>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
